import { MouseEventHandler, useContext, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import CircularProgress from '@mui/material/CircularProgress'
import styles from './styles.module.css'
import PrimaryButton from '../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../global/components/buttons/secondary-button/SecondaryButton'
import useFetch from '../../../../global/hooks/useFetchHook'
import { fetchData } from '../../../../global/utils/fetch'
import ToastNotification, {
  IAlertStatusProps,
} from '../../../../global/components/toast-alert/ToastNotification'
import { IUser } from '../../../../../../app/entities/User'
import { ILocation } from '../../../../../../app/entities/Location'
import { IOnCallTechnicianManagerOverride } from '../../../../../../app/entities/OnCallTechnicianManagerOverride'
import { ITechnicianManagerCoverage } from '../../../../../../app/entities/TechnicianManagerCoverage'
import {
  getTechnicianManagers,
  setOnCallTechnicianManagerOverride,
  getAllLocationsWithCurrentManagers,
  getAllOnCallOverrides,
  deleteOnCallOverride,
  updateOnCallOverride,
  getAllTechnicianManagerCoverages,
  createTechnicianManagerCoverage,
  deleteTechnicianManagerCoverage,
  ITechnicianManagerCoverageData,
} from '../api'
import { OVERRIDE_STATUS } from '../../../../global/constants/on-call-override'
import { UserContext } from '../../../../base/components/base-container/BaseContainer'
import hasPermission from '../../../../global/utils/user/has-permission'
import { PERMISSIONS } from '../../../../global/constants/permissions'

interface IOnCallTechnicianManagerModalProps {
  isOpen: boolean
  handleClosed: MouseEventHandler
}

interface IFormData {
  technicianManagerObjectId: string
  fromDate: string
  toDate: string
}


export default function OnCallTechnicianManagerModal({
  isOpen,
  handleClosed,
}: IOnCallTechnicianManagerModalProps) {
  const { user } = useContext(UserContext)
  const canEdit = hasPermission(user).edit(
    PERMISSIONS.TECHNICIANS.CATEGORY,
    PERMISSIONS.TECHNICIANS.ENTRIES.ON_CALL_TECHNICIAN_MANAGER.NAME,
  )

  const formatDate = (dateString: string): string => {
    if (!dateString) return ''
    const [year, month, day] = dateString.split('-')
    return `${month}/${day}/${year}`
  }

  const getTodayDateString = (): string => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const getOverrideStatus = (
    override: IOnCallTechnicianManagerOverride | ITechnicianManagerCoverage,
  ): string => {
    const today = getTodayDateString()

    if (today < override.fromDate) {
      return OVERRIDE_STATUS.SCHEDULED
    }

    if (today >= override.fromDate && today <= override.toDate) {
      return OVERRIDE_STATUS.ACTIVE_NOW
    }

    return OVERRIDE_STATUS.SCHEDULED
  }

  // Tab: 0 = View Managers, 1 = Create/Edit On-Call Override, 2 = Create Coverage
  const [activeTab, setActiveTab] = useState<number>(0)
  const [formData, setFormData] = useState<IFormData>({
    technicianManagerObjectId: '',
    fromDate: '',
    toDate: '',
  })
  const [coverageFormData, setCoverageFormData] = useState<ITechnicianManagerCoverageData>({
    originalManagerObjectId: '',
    coverageManagerObjectId: '',
    fromDate: '',
    toDate: '',
  })
  const [editingOverrideObjectId, setEditingOverrideObjectId] = useState<
    string | null
  >(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [alertStatus, setAlertStatus] = useState<IAlertStatusProps>({
    isOpen: false,
    message: '',
    severity: 'success',
  })

  // Fetch users with 'tech-manager' role
  const { data: technicianManagers, isLoading } = useFetch<IUser[]>(
    getTechnicianManagers(),
  )

  // Fetch all locations with their current managers
  const { data: locationsWithManagers, isLoading: isLoadingLocations } =
    useFetch<ILocation[]>(getAllLocationsWithCurrentManagers())

  // Fetch all on-call overrides
  const { data: onCallOverrides, isLoading: isLoadingOverrides } = useFetch<{
    currentOnCallTechnicianManagerOverride:
      | IOnCallTechnicianManagerOverride
      | undefined
    allOnCallTechnicianManagerOverrides: IOnCallTechnicianManagerOverride[]
    isOnCallTime: boolean
  }>(getAllOnCallOverrides())

  // Fetch all technician manager coverages
  const { data: coverages, isLoading: isLoadingCoverages } = useFetch<
    ITechnicianManagerCoverage[]
  >(getAllTechnicianManagerCoverages())

  // Manual data state for refreshing after save/delete
  const [manualLocationsData, setManualLocationsData] = useState<
    ILocation[] | null
  >(null)
  const [manualOverridesData, setManualOverridesData] = useState<{
    currentOnCallTechnicianManagerOverride:
      | IOnCallTechnicianManagerOverride
      | undefined
    allOnCallTechnicianManagerOverrides: IOnCallTechnicianManagerOverride[]
    isOnCallTime: boolean
  } | null>(null)
  const [manualCoveragesData, setManualCoveragesData] = useState<
    ITechnicianManagerCoverage[] | null
  >(null)

  const refreshData = async () => {
    try {
      const [newLocations, newOverrides, newCoverages] = await Promise.all([
        fetchData<ILocation[]>(getAllLocationsWithCurrentManagers()),
        fetchData<{
          currentOnCallTechnicianManagerOverride:
            | IOnCallTechnicianManagerOverride
            | undefined
          allOnCallTechnicianManagerOverrides: IOnCallTechnicianManagerOverride[]
          isOnCallTime: boolean
        }>(getAllOnCallOverrides()),
        fetchData<ITechnicianManagerCoverage[]>(
          getAllTechnicianManagerCoverages(),
        ),
      ])
      setManualLocationsData(newLocations)
      setManualOverridesData(newOverrides)
      setManualCoveragesData(newCoverages)
    } catch (error) {
      console.error('Failed to refresh data:', error)
    }
  }

  const displayLocations = manualLocationsData ?? locationsWithManagers
  const displayOverrides = manualOverridesData ?? onCallOverrides
  const displayCoverages = manualCoveragesData ?? coverages

  const handleAlertClosed = () => {
    setAlertStatus({ ...alertStatus, isOpen: false })
  }

  const isFormValid = (): boolean => {
    if (
      !formData.technicianManagerObjectId ||
      !formData.fromDate ||
      !formData.toDate
    ) {
      return false
    }
    return formData.toDate >= formData.fromDate
  }

  const isCoverageFormValid = (): boolean => {
    if (
      !coverageFormData.originalManagerObjectId ||
      !coverageFormData.coverageManagerObjectId ||
      !coverageFormData.fromDate ||
      !coverageFormData.toDate
    ) {
      return false
    }
    if (
      coverageFormData.originalManagerObjectId ===
      coverageFormData.coverageManagerObjectId
    ) {
      return false
    }
    return coverageFormData.toDate >= coverageFormData.fromDate
  }

  const handleEdit = (override: IOnCallTechnicianManagerOverride) => {
    setEditingOverrideObjectId(override.objectId)
    setFormData({
      technicianManagerObjectId: override.technicianManager.objectId,
      fromDate: override.fromDate,
      toDate: override.toDate,
    })
    setActiveTab(1)
  }

  const resetForm = () => {
    setEditingOverrideObjectId(null)
    setFormData({
      technicianManagerObjectId: '',
      fromDate: '',
      toDate: '',
    })
    setCoverageFormData({
      originalManagerObjectId: '',
      coverageManagerObjectId: '',
      fromDate: '',
      toDate: '',
    })
  }

  const handleSave = async () => {
    if (!isFormValid()) {
      setAlertStatus({
        isOpen: true,
        message:
          'Please fill in all required fields and ensure dates are valid',
        severity: 'error',
      })
      return
    }

    setIsSubmitting(true)
    try {
      if (editingOverrideObjectId) {
        await fetchData(
          updateOnCallOverride(editingOverrideObjectId, formData),
        )
      } else {
        await fetchData(setOnCallTechnicianManagerOverride(formData))
      }

      await refreshData()
      resetForm()

      setAlertStatus({
        isOpen: true,
        message: editingOverrideObjectId
          ? 'On-call technician manager override updated successfully'
          : 'On-call technician manager override saved successfully',
        severity: 'success',
      })

      setActiveTab(0)
    } catch (error: any) {
      console.error('Save failed:', error)
      const errorMessage =
        error?.message ||
        error?.toString() ||
        'Failed to save on-call technician manager override'
      setAlertStatus({
        isOpen: true,
        message: `Failed to save: ${errorMessage}`,
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSaveCoverage = async () => {
    if (!isCoverageFormValid()) {
      setAlertStatus({
        isOpen: true,
        message:
          'Please fill in all required fields. Original and coverage manager must be different.',
        severity: 'error',
      })
      return
    }

    setIsSubmitting(true)
    try {
      await fetchData(createTechnicianManagerCoverage(coverageFormData))

      await refreshData()
      resetForm()

      setAlertStatus({
        isOpen: true,
        message: 'Manager coverage created successfully',
        severity: 'success',
      })

      setActiveTab(0)
    } catch (error: any) {
      console.error('Save coverage failed:', error)
      const errorMessage =
        error?.message ||
        error?.toString() ||
        'Failed to create manager coverage'
      setAlertStatus({
        isOpen: true,
        message: `Failed to save: ${errorMessage}`,
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (
    overrideObjectId: string,
    managerName: string,
  ) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the override for ${managerName}?`,
      )
    ) {
      return
    }

    try {
      await fetchData(deleteOnCallOverride(overrideObjectId))
      await refreshData()

      setAlertStatus({
        isOpen: true,
        message: 'Override deleted successfully',
        severity: 'success',
      })
    } catch (error: any) {
      console.error('Delete failed:', error)
      const errorMessage =
        error?.message || error?.toString() || 'Failed to delete override'
      setAlertStatus({
        isOpen: true,
        message: `Failed to delete: ${errorMessage}`,
        severity: 'error',
      })
    }
  }

  const handleDeleteCoverage = async (
    coverageObjectId: string,
    originalManagerName: string,
  ) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the coverage for ${originalManagerName}?`,
      )
    ) {
      return
    }

    try {
      await fetchData(deleteTechnicianManagerCoverage(coverageObjectId))
      await refreshData()

      setAlertStatus({
        isOpen: true,
        message: 'Coverage deleted successfully',
        severity: 'success',
      })
    } catch (error: any) {
      console.error('Delete coverage failed:', error)
      const errorMessage =
        error?.message || error?.toString() || 'Failed to delete coverage'
      setAlertStatus({
        isOpen: true,
        message: `Failed to delete: ${errorMessage}`,
        severity: 'error',
      })
    }
  }

  const handleFormChange = (field: keyof IFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleCoverageFormChange = (
    field: keyof ITechnicianManagerCoverageData,
    value: string,
  ) => {
    setCoverageFormData((prev) => ({ ...prev, [field]: value }))
  }

  // Find active coverage for a given manager objectId
  const getActiveCoverageForManager = (
    managerObjectId: string,
  ): ITechnicianManagerCoverage | undefined => {
    if (!displayCoverages) return undefined
    const today = getTodayDateString()
    return displayCoverages.find(
      (c) =>
        c.originalManager.objectId === managerObjectId &&
        today >= c.fromDate &&
        today <= c.toDate,
    )
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={(e) => {
          resetForm()
          handleClosed(e as any)
        }}
      >
        <Box className={styles.modalContainer}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>
              Technician Manager Override
            </h2>
            <p className={styles.modalDescription}>
              Manage on-call and coverage overrides for technician managers.
            </p>

            <div className={styles.tabContainer}>
              {activeTab === 0 ? (
                <PrimaryButton
                  buttonName='View Managers'
                  onClick={() => setActiveTab(0)}
                />
              ) : (
                <SecondaryButton
                  buttonName='View Managers'
                  onClick={() => setActiveTab(0)}
                />
              )}
              {activeTab === 1 ? (
                <PrimaryButton
                  buttonName={
                    editingOverrideObjectId
                      ? 'Edit On-Call Override'
                      : 'On-Call Override'
                  }
                  onClick={() => setActiveTab(1)}
                />
              ) : (
                <SecondaryButton
                  buttonName='On-Call Override'
                  onClick={() => {
                    resetForm()
                    setActiveTab(1)
                  }}
                  disabled={!canEdit}
                />
              )}
              {activeTab === 2 ? (
                <PrimaryButton
                  buttonName='Manager Coverage'
                  onClick={() => setActiveTab(2)}
                />
              ) : (
                <SecondaryButton
                  buttonName='Manager Coverage'
                  onClick={() => {
                    resetForm()
                    setActiveTab(2)
                  }}
                  disabled={!canEdit}
                />
              )}
            </div>

            <div className={styles.contentArea}>
              {activeTab === 0 ? (
                <div>
                  {isLoadingLocations ||
                  isLoadingOverrides ||
                  isLoadingCoverages ? (
                    <div className={styles.loadingContainer}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <>
                      {/* Current On-Call Manager Section */}
                      {displayOverrides?.allOnCallTechnicianManagerOverrides &&
                      displayOverrides.allOnCallTechnicianManagerOverrides
                        .length > 0 ? (
                        <div className={styles.currentOnCallSection}>
                          <h3 className={styles.sectionTitle}>
                            Current On-Call Manager
                          </h3>
                          <div className={styles.currentOnCallInfo}>
                            {(() => {
                              const today = getTodayDateString()
                              const currentOverride =
                                displayOverrides.allOnCallTechnicianManagerOverrides.find(
                                  (o: IOnCallTechnicianManagerOverride) => {
                                    return (
                                      today >= o.fromDate &&
                                      today <= o.toDate
                                    )
                                  },
                                )

                              if (!currentOverride) {
                                return (
                                  <p className={styles.modalDescription}>
                                    No override scheduled. Technicians use
                                    their location managers.
                                  </p>
                                )
                              }

                              return (
                                <div
                                  {...(canEdit && {
                                    className: styles.clickableRow,
                                    onClick: () =>
                                      handleEdit(currentOverride),
                                    title: 'Click to edit this override',
                                  })}
                                >
                                  <p>
                                    <strong>
                                      {
                                        currentOverride.technicianManager
                                          .name
                                      }
                                    </strong>
                                  </p>
                                  <p className={styles.modalDescription}>
                                    Active:{' '}
                                    {formatDate(currentOverride.fromDate)} -{' '}
                                    {formatDate(currentOverride.toDate)}
                                  </p>
                                  {displayOverrides.isOnCallTime && (
                                    <span
                                      className={styles.overrideBadge}
                                    >
                                      ON-CALL HOURS
                                    </span>
                                  )}
                                </div>
                              )
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className={styles.currentOnCallSection}>
                          <h3 className={styles.sectionTitle}>
                            Current On-Call Manager
                          </h3>
                          <p className={styles.modalDescription}>
                            No override scheduled. Technicians use their
                            location managers.
                          </p>
                        </div>
                      )}

                      {/* Location Managers Table */}
                      <div className={styles.sectionDivider}>
                        <h3 className={styles.sectionTitle}>
                          Technician Managers by Location
                        </h3>
                        <p className={styles.modalDescription}>
                          Default technician managers for each location
                          (used during business hours).
                        </p>
                        {displayLocations &&
                        displayLocations.length > 0 ? (
                          <table className={styles.assignmentsTable}>
                            <thead>
                              <tr>
                                <th>Location</th>
                                <th>Technician Manager</th>
                                <th>Active Coverage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {displayLocations.map((location) => {
                                const activeCoverage = location
                                  .technicianManager
                                  ? getActiveCoverageForManager(
                                      location.technicianManager.objectId,
                                    )
                                  : undefined

                                return (
                                  <tr key={location.objectId}>
                                    <td>{location.name}</td>
                                    <td>
                                      {location.technicianManager?.name ||
                                        'None'}
                                    </td>
                                    <td>
                                      {activeCoverage ? (
                                        <>
                                          <span
                                            className={
                                              styles.overrideBadge
                                            }
                                          >
                                            {
                                              activeCoverage.coverageManager
                                                .name
                                            }
                                          </span>
                                          <p
                                            className={
                                              styles.modalDescription
                                            }
                                          >
                                            {formatDate(
                                              activeCoverage.fromDate,
                                            )}{' '}
                                            -{' '}
                                            {formatDate(
                                              activeCoverage.toDate,
                                            )}
                                          </p>
                                        </>
                                      ) : (
                                        '-'
                                      )}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <p>No locations found</p>
                        )}
                      </div>

                      {/* Scheduled On-Call Overrides Table */}
                      {displayOverrides?.allOnCallTechnicianManagerOverrides &&
                      displayOverrides
                        .allOnCallTechnicianManagerOverrides.length > 0 ? (
                        <div className={styles.sectionDivider}>
                          <h3 className={styles.sectionTitle}>
                            On-Call Overrides
                          </h3>
                          <p className={styles.modalDescription}>
                            On-call manager overrides (active during
                            after-hours and weekends only).
                          </p>
                          <table className={styles.assignmentsTable}>
                            <thead>
                              <tr>
                                <th>Manager</th>
                                <th>From Date</th>
                                <th>To Date</th>
                                <th>Status</th>
                                {canEdit && <th>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {displayOverrides.allOnCallTechnicianManagerOverrides.map(
                                (
                                  override: IOnCallTechnicianManagerOverride,
                                ) => (
                                  <tr
                                    key={override.objectId}
                                    {...(canEdit && {
                                      className: styles.clickableRow,
                                      onClick: () =>
                                        handleEdit(override),
                                      title:
                                        'Click to edit this override',
                                    })}
                                  >
                                    <td>
                                      {override.technicianManager.name}
                                    </td>
                                    <td>
                                      {formatDate(override.fromDate)}
                                    </td>
                                    <td>
                                      {formatDate(override.toDate)}
                                    </td>
                                    <td>
                                      {getOverrideStatus(override) ===
                                      OVERRIDE_STATUS.ACTIVE_NOW ? (
                                        <span
                                          className={
                                            styles.overrideBadge
                                          }
                                        >
                                          {getOverrideStatus(override)}
                                        </span>
                                      ) : (
                                        <span>
                                          {getOverrideStatus(override)}
                                        </span>
                                      )}
                                    </td>
                                    {canEdit && (
                                      <td>
                                        <SecondaryButton
                                          buttonName='Delete'
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            handleDelete(
                                              override.objectId,
                                              override.technicianManager
                                                .name,
                                            )
                                          }}
                                        />
                                      </td>
                                    )}
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : null}

                      {/* Manager Coverage Table */}
                      {displayCoverages &&
                      displayCoverages.length > 0 ? (
                        <div className={styles.sectionDivider}>
                          <h3 className={styles.sectionTitle}>
                            Manager Coverage
                          </h3>
                          <p className={styles.modalDescription}>
                            Temporary manager replacements. Technicians
                            will see the coverage manager instead of the
                            original for the date range.
                          </p>
                          <table className={styles.assignmentsTable}>
                            <thead>
                              <tr>
                                <th>Original Manager</th>
                                <th>Covered By</th>
                                <th>From Date</th>
                                <th>To Date</th>
                                <th>Status</th>
                                {canEdit && <th>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {displayCoverages.map(
                                (
                                  coverage: ITechnicianManagerCoverage,
                                ) => (
                                  <tr key={coverage.objectId}>
                                    <td>
                                      {coverage.originalManager.name}
                                    </td>
                                    <td>
                                      {coverage.coverageManager.name}
                                    </td>
                                    <td>
                                      {formatDate(coverage.fromDate)}
                                    </td>
                                    <td>
                                      {formatDate(coverage.toDate)}
                                    </td>
                                    <td>
                                      {getOverrideStatus(coverage) ===
                                      OVERRIDE_STATUS.ACTIVE_NOW ? (
                                        <span
                                          className={
                                            styles.overrideBadge
                                          }
                                        >
                                          {getOverrideStatus(coverage)}
                                        </span>
                                      ) : (
                                        <span>
                                          {getOverrideStatus(coverage)}
                                        </span>
                                      )}
                                    </td>
                                    {canEdit && (
                                      <td>
                                        <SecondaryButton
                                          buttonName='Delete'
                                          onClick={() =>
                                            handleDeleteCoverage(
                                              coverage.objectId,
                                              coverage.originalManager
                                                .name,
                                            )
                                          }
                                        />
                                      </td>
                                    )}
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              ) : activeTab === 1 ? (
                <>
                  {isLoading ? (
                    <div className={styles.progressContainer}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <div className={styles.formContainer}>
                      <p className={styles.modalDescription}>
                        This manager will handle ALL technicians during
                        after-hours (5pm-4am EST) and weekends within the
                        selected date range.
                      </p>

                      <div className={styles.inputField}>
                        <TextField
                          select
                          label='Select Technician Manager *'
                          value={formData.technicianManagerObjectId}
                          onChange={(e) =>
                            handleFormChange(
                              'technicianManagerObjectId',
                              e.target.value,
                            )
                          }
                          fullWidth
                          disabled={
                            !technicianManagers ||
                            technicianManagers.length === 0
                          }
                          InputLabelProps={{ shrink: true }}
                        >
                          {technicianManagers &&
                          technicianManagers.length > 0 ? (
                            technicianManagers.map((manager) => (
                              <MenuItem
                                key={manager.objectId}
                                value={manager.objectId}
                              >
                                {manager.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value=''>
                              No technician managers available
                            </MenuItem>
                          )}
                        </TextField>
                      </div>

                      <div className={styles.dateFieldsContainer}>
                        <div className={styles.dateField}>
                          <TextField
                            type='date'
                            label='From Date *'
                            name='fromDate'
                            value={formData.fromDate}
                            onChange={(e) =>
                              handleFormChange(
                                'fromDate',
                                e.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </div>

                        <div className={styles.dateField}>
                          <TextField
                            type='date'
                            label='To Date *'
                            name='toDate'
                            value={formData.toDate}
                            onChange={(e) =>
                              handleFormChange(
                                'toDate',
                                e.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {isLoading ? (
                    <div className={styles.progressContainer}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <div className={styles.formContainer}>
                      <p className={styles.modalDescription}>
                        If a technician manager calls out, select them as
                        the original manager and choose a replacement. All
                        locations assigned to the original manager will
                        show the coverage manager instead for the selected
                        date range. During on-call hours, an on-call
                        override takes priority if one is set.
                      </p>

                      <div className={styles.inputField}>
                        <TextField
                          select
                          label='Original Manager (calling out) *'
                          value={
                            coverageFormData.originalManagerObjectId
                          }
                          onChange={(e) =>
                            handleCoverageFormChange(
                              'originalManagerObjectId',
                              e.target.value,
                            )
                          }
                          fullWidth
                          disabled={
                            !technicianManagers ||
                            technicianManagers.length === 0
                          }
                          InputLabelProps={{ shrink: true }}
                        >
                          {technicianManagers &&
                          technicianManagers.length > 0 ? (
                            technicianManagers.map((manager) => (
                              <MenuItem
                                key={manager.objectId}
                                value={manager.objectId}
                              >
                                {manager.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value=''>
                              No technician managers available
                            </MenuItem>
                          )}
                        </TextField>
                      </div>

                      <div className={styles.inputField}>
                        <TextField
                          select
                          label='Coverage Manager (replacement) *'
                          value={
                            coverageFormData.coverageManagerObjectId
                          }
                          onChange={(e) =>
                            handleCoverageFormChange(
                              'coverageManagerObjectId',
                              e.target.value,
                            )
                          }
                          fullWidth
                          disabled={
                            !technicianManagers ||
                            technicianManagers.length === 0
                          }
                          InputLabelProps={{ shrink: true }}
                        >
                          {technicianManagers &&
                          technicianManagers.length > 0 ? (
                            technicianManagers
                              .filter(
                                (m) =>
                                  m.objectId !==
                                  coverageFormData.originalManagerObjectId,
                              )
                              .map((manager) => (
                                <MenuItem
                                  key={manager.objectId}
                                  value={manager.objectId}
                                >
                                  {manager.name}
                                </MenuItem>
                              ))
                          ) : (
                            <MenuItem value=''>
                              No technician managers available
                            </MenuItem>
                          )}
                        </TextField>
                      </div>

                      <div className={styles.dateFieldsContainer}>
                        <div className={styles.dateField}>
                          <TextField
                            type='date'
                            label='From Date *'
                            name='fromDate'
                            value={coverageFormData.fromDate}
                            onChange={(e) =>
                              handleCoverageFormChange(
                                'fromDate',
                                e.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </div>

                        <div className={styles.dateField}>
                          <TextField
                            type='date'
                            label='To Date *'
                            name='toDate'
                            value={coverageFormData.toDate}
                            onChange={(e) =>
                              handleCoverageFormChange(
                                'toDate',
                                e.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={styles.buttonsContainer}>
              <SecondaryButton
                buttonName='Cancel'
                onClick={(e) => {
                  resetForm()
                  handleClosed(e)
                }}
                disabled={isSubmitting}
              />
              {activeTab === 1 && (
                <PrimaryButton
                  buttonName={
                    isSubmitting
                      ? 'Saving...'
                      : editingOverrideObjectId
                        ? 'Update Override'
                        : 'Save Override'
                  }
                  onClick={handleSave}
                  disabled={isSubmitting || !isFormValid()}
                />
              )}
              {activeTab === 2 && (
                <PrimaryButton
                  buttonName={
                    isSubmitting ? 'Saving...' : 'Save Coverage'
                  }
                  onClick={handleSaveCoverage}
                  disabled={isSubmitting || !isCoverageFormValid()}
                />
              )}
            </div>
          </div>
        </Box>
      </Modal>

      <ToastNotification
        open={alertStatus.isOpen}
        message={alertStatus.message}
        severity={alertStatus.severity}
        handleClose={handleAlertClosed}
      />
    </>
  )
}
